import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - package ships its own types inconsistently across versions
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';

import {
  API_BASE_URL,
  COGNITO_USER_POOL_ID,
  COGNITO_CLIENT_ID,
  COGNITO_DOMAIN,
  COGNITO_REDIRECT_URI,
} from '../../config';
import { AuthShell } from '../../components/AuthShell';

type UserMe = {
  tenantId: string;
  orgName: string;
  userId: string;
  name: string;
  role: string;
  plan?: string;
};

async function fetchUserMe(idToken: string): Promise<UserMe | null> {
  const resp = await fetch(`${API_BASE_URL}/users/me`, {
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (resp.status === 404) return null;
  if (!resp.ok) throw new Error('Failed to fetch user profile');
  return (await resp.json()) as UserMe;
}

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debugAuthEnabled =
    searchParams.get('debugAuth') === '1' ||
    (() => {
      try {
        return localStorage.getItem('nimbus:debugAuth') === '1';
      } catch {
        return false;
      }
    })();

  const maskedClientId = useMemo(() => {
    const raw = COGNITO_CLIENT_ID || '';
    if (raw.length <= 10) return raw;
    return `${raw.slice(0, 6)}…${raw.slice(-4)}`;
  }, []);

  const userPool = useMemo(() => {
    return new CognitoUserPool({
      UserPoolId: COGNITO_USER_POOL_ID,
      ClientId: COGNITO_CLIENT_ID,
    });
  }, []);

  const redirectAfterLogin = () => {
    const redirectPath = sessionStorage.getItem('redirectAfterLogin');
    sessionStorage.removeItem('redirectAfterLogin');

    if (redirectPath && redirectPath.startsWith('/') && !redirectPath.startsWith('/login')) {
      navigate(redirectPath);
      return;
    }

    navigate('/dashboard');
  };

  const handleLogin = async () => {
    setError('');
    setIsSubmitting(true);

    try {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      await new Promise<void>((resolve, reject) => {
        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: async (result: any) => {
            try {

              const idToken = result.getIdToken().getJwtToken();
              const accessToken = result.getAccessToken().getJwtToken();
              const refreshToken = result.getRefreshToken().getToken();

              localStorage.setItem('idToken', idToken);
              localStorage.setItem('accessToken', accessToken);
              localStorage.setItem('refreshToken', refreshToken);
              localStorage.setItem('userEmail', email);

              // Decode groups from ID token and set superadmin flag
              try {
                const payload = JSON.parse(atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
                const groups: string[] = payload['cognito:groups'] || [];
                localStorage.setItem('isSuperAdmin', groups.includes('salvinta-superadmin') ? 'true' : 'false');
              } catch {
                localStorage.setItem('isSuperAdmin', 'false');
              }

              const me = await fetchUserMe(idToken);

              if (!me) {
                navigate('/no-access');
                resolve();
                return;
              }

              localStorage.setItem('tenantId', me.tenantId);
              localStorage.setItem('orgName', me.orgName);
              localStorage.setItem('userId', me.userId);
              localStorage.setItem('userName', me.name);
              localStorage.setItem('userRole', me.role);
              localStorage.setItem('userPlan', me.plan || 'basic');

              redirectAfterLogin();
              resolve();
            } catch (e) {
              console.error('Error in onSuccess handler:', e);
              reject(e);
            }
          },
          onFailure: (err: any) => {
            console.error('Cognito authentication failed:', err);
            reject(err);
          },
          newPasswordRequired: () => {
            sessionStorage.setItem('tempEmail', email);
            sessionStorage.setItem('tempPassword', password);
            navigate('/set-password');
            resolve();
          },
        });
      });
    } catch (e: any) {
      console.error('Login error caught:', e);
      const msg = e?.message || 'Login failed. Please try again.';
      setError(msg);
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(false);
  };

  const loginWithGoogle = () => {
    if (!COGNITO_DOMAIN) {
      setError('Google sign-in is not configured for this environment.');
      return;
    }

    const authUrl =
      `https://${COGNITO_DOMAIN}/oauth2/authorize?` +
      `client_id=${encodeURIComponent(COGNITO_CLIENT_ID)}&` +
      `response_type=code&` +
      `scope=openid+email+profile&` +
      `redirect_uri=${encodeURIComponent(COGNITO_REDIRECT_URI)}&` +
      `identity_provider=Google`;

    window.location.href = authUrl;
  };

  useEffect(() => {
    const auto = searchParams.get('auto');
    const storedEmail = sessionStorage.getItem('signupEmail') || '';
    const storedPassword = sessionStorage.getItem('signupPassword') || '';

    if (auto === 'true' && storedEmail && storedPassword) {
      sessionStorage.removeItem('signupEmail');
      sessionStorage.removeItem('signupPassword');
      setEmail(storedEmail);
      setPassword(storedPassword);
      setTimeout(() => void handleLogin(), 250);
    }
  }, [searchParams]);

  return (
    <AuthShell>
      <div className="auth-heading">
        <h3>Welcome back</h3>
        <p>Sign in to your account</p>
      </div>

      {debugAuthEnabled ? (
        <div className="np-banner np-tone-warning mb-3" role="alert" style={{ display: 'block' }}>
          <div className="fw-semibold mb-1">Auth debug (opt-in)</div>
          <div className="small">
            <div>
              <strong>COGNITO_DOMAIN:</strong> {COGNITO_DOMAIN || '(not set)'}
            </div>
            <div>
              <strong>COGNITO_REDIRECT_URI:</strong> {COGNITO_REDIRECT_URI || '(not set)'}
            </div>
            <div>
              <strong>COGNITO_LOGOUT_URI:</strong> {import.meta.env.VITE_COGNITO_LOGOUT_URI || '(default from FRONTEND_URL)'}
            </div>
            <div>
              <strong>COGNITO_CLIENT_ID:</strong> {maskedClientId || '(not set)'}
            </div>
          </div>
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleLogin();
        }}
      >
        <div className="np-field">
          <label>Email</label>
          <input
            type="email"
            className="np-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
          />
        </div>
        <div className="np-field">
          <label>Password</label>
          <input
            type="password"
            className="np-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        {error ? (
          <div className="np-banner np-tone-danger mb-3"><i className="ri-error-warning-line"></i>{error}</div>
        ) : null}

        <button type="submit" className="np-btn np-btn-primary w-100 justify-content-center" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm" />
              Signing in…
            </>
          ) : (
            <>
              <i className="ri-login-box-line"></i>
              Sign In
            </>
          )}
        </button>
      </form>

      <div className="auth-divider">or</div>

      <button type="button" onClick={loginWithGoogle} className="np-btn np-btn-ghost w-100 justify-content-center">
        <i className="ri-google-fill"></i>
        Continue with Google
      </button>

      <p className="np-sub text-center mt-4 mb-0">Need access? Contact your organization administrator for an invitation.</p>
    </AuthShell>
  );
};
